import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FeatureFlagService } from '@inyova/inyova-shared';
import { Account, Customer, CustomerAccountItem, FeatureFlags, StepStatus } from '@inyova/models';
import { isMinAlmostCustomer } from '@inyova/utils';
import { TranslateService } from '@ngx-translate/core';
import { NgxZendeskWebwidgetService } from 'ngx-zendesk-webwidget';

import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as AppRootActions from '@app/app.actions';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { APP_COUNTRIES, MOBILE_NAVIGATION, MOBILE_VIEW_TRESHOLD } from '@app/app.constants';
import { AuthService } from '@shared/services/auth.service';
import { LanguageListItem, LanguageService, Languages } from '@shared/services/language.service';
import { TrackingService } from '@shared/services/tracking.service';
import { environment } from 'src/environments/environment';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-navbar',
  styleUrls: ['./navbar.component.scss'],
  templateUrl: './navbar.component.html',
  standalone: false
})
export class NavbarComponent implements OnDestroy, OnInit {
  activeHamburger = false;
  activeAccountSwitch = false;
  currentLanguage: string;
  customer: Customer;
  currentAccount: Account;
  mobileView = false;
  languages: LanguageListItem[];
  domain: Customer['app_location'];
  appLocation: string;
  logoSrc: string;
  openDropdown = false;
  strategyWasChanged: { modalOpened: boolean; redirectUrl: string; strategyWasChanged: boolean };
  kidsAccounts: CustomerAccountItem[];
  switchAccountSpinner = false;
  switchAccountSpinnerMessage = '';
  switchAccountOpened = false;
  onProductSwitch = false;
  oddActive = false;

  isLoggedIn: boolean;
  isRiskRecheck = false;

  isCountrySwitchOpen = false;

  isCountryListOpen = false;
  selectedCountry: { flag: string; key: string };
  appCountries = APP_COUNTRIES;

  isLanguageListOpen = false;
  selectedLanguage: LanguageListItem;

  whiteListPageURLS = ['/product', '/help', '/new-account', '/profile/settings', '/profile/documents', '/tell-friend'];

  isCIActive = false;
  isLoading = false;

  growAccountStepStatus: StepStatus | undefined;

  protected readonly onDestroy$ = new Subject<void>();

  get showNavigation() {
    return (
      !this.switchAccountSpinner &&
      !this.onProductSwitch &&
      (!this.customer.hide_personalisation ||
        this.currentAccount.ql_onboarding_step > 1 ||
        (this.currentAccount.ql_onboarding_step === 1 && this.currentAccount.step_status === 5)) &&
      !this.router.url.includes('/grow/intro')
    );
  }

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private store: Store,
    private router: Router,
    private translateService: TranslateService,
    private trackingService: TrackingService,
    private zendeskService: NgxZendeskWebwidgetService,
    private featureFlagService: FeatureFlagService
  ) {
    this.isRiskRecheck = window.location.href.includes('risk-check');

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.isRiskRecheck = event.url.includes('risk-check');
      });

    this.isLoggedIn = this.authService.isLoggedIn.getValue();

    this.languageService.selected.pipe(takeUntil(this.onDestroy$)).subscribe((lang) => {
      const langCountry = lang.split('-');
      // eslint-disable-next-line prefer-destructuring
      this.currentLanguage = langCountry[0];
      this.domain = langCountry[1].toLowerCase() as Customer['app_location'];
      this.languages = this.languageService.getListOfLanguages(this.domain) as LanguageListItem[];

      this.selectedLanguage = this.languages.find((item: LanguageListItem) => item.code === `${this.currentLanguage}-${this.domain.toUpperCase()}`);
      this.selectedCountry = APP_COUNTRIES.find((item) => item.flag === this.domain);
    });

    this.store.pipe(select(fromAppRoot.selectCustomer), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.customer = value;
      this.appLocation = value.app_location;
      this.growAccountStepStatus = this.findCustomerAccount('grow')?.step_status;
    });
    this.store.pipe(select(fromAppRoot.selectSwitchAccountLoading), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.switchAccountSpinner = value.show;
      this.switchAccountSpinnerMessage = value.message;
    });
    this.store.pipe(select(fromAppRoot.selectCurrentAccount), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.currentAccount = value;

      this.logoSrc = this.getLogoSource();

      this.mobileView = window.innerWidth <= (this.currentAccount.step_status === 5 ? MOBILE_NAVIGATION : MOBILE_VIEW_TRESHOLD);
      // Change language if needed
      if (this.currentAccount.step_status && this.customer.language !== this.translateService.currentLang) {
        this.languageService.setLanguage(this.customer.language as Languages);
      }
      this.activeHamburger = false;
    });

    this.store.pipe(select(fromAppRoot.selectKidsAccounts), takeUntil(this.onDestroy$)).subscribe((kidAccounts) => {
      this.kidsAccounts = kidAccounts;
    });

    this.store.pipe(select(fromStrategy.selectStrategyWasChanged), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.strategyWasChanged = value;
    });

    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((value) => {
      if (typeof this.currentAccount.step_status === 'number' && this.currentAccount.step_status < 1 && !this.customer.has_crowd_investor_account) {
        this.onProductSwitch = this.whiteListPageURLS.includes((value as NavigationEnd).url);
      } else {
        this.onProductSwitch = false;
      }
    });

    this.store.pipe(select(fromAppRoot.selectIsCIActive), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.isCIActive = value;
      this.logoSrc = this.getLogoSource();
    });

    this.store.pipe(select(fromAppRoot.selectCreateNewAccountIsLoading), takeUntil(this.onDestroy$)).subscribe((isLoading) => {
      this.isLoading = isLoading;
    });

    this.store
      .select(fromAppRoot.selectOddPageActive)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((oddActive) => (this.oddActive = oddActive));
  }

  ngOnInit(): void {
    if (typeof this.currentAccount.step_status === 'number' && this.currentAccount.step_status < 1 && !this.customer.has_crowd_investor_account) {
      this.onProductSwitch = this.whiteListPageURLS.includes(this.router.url);
    } else {
      this.onProductSwitch = false;
    }
  }

  getLogoSource(): string {
    if (this.currentAccount.kind === 'kid' && !this.isCIActive) {
      return environment.I4KlogoURL;
    }

    return environment.logoURL;
  }

  goTo(path: string): void {
    if (this.checkWasChanged(path)) {
      return;
    }

    if (this.activeHamburger) {
      void this.router.navigateByUrl(path).then(() => this.toggleMenu());
    } else {
      void this.router.navigateByUrl(path);
    }

    // update strategy tab index
    this.openStrategyPortfolio(path);
  }

  openStrategyPortfolio(path: string): void {
    if (!path.includes('strategy')) {
      return;
    }
    const tabIndex = this.currentAccount.step_status !== 1 ? 5 : this.currentAccount.ql_onboarding_step;
    this.store.dispatch(StrategyActions.setTabIndex({ tabIndex }));
  }

  goToExternal(url: string, target = '_blank'): Window {
    if (this.mobileView) {
      this.toggleMenu();
    }
    return window.open(url, target);
  }

  openFAQ(): void {
    if (this.mobileView) {
      this.toggleMenu();
    }
    const url = `https://help.inyova.${this.appLocation}/hc/${this.currentLanguage}`;
    window.open(url, 'blank');
  }

  isActive(path: string): boolean {
    if (this.router.url.includes('/profile')) {
      return this.router.url === path;
    }
    return this.router.url.includes(path);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  logoRedirect(): void {
    if (this.isCIActive) {
      void this.router.navigate(['/ci-overview']);
      return;
    }

    if (!this.isLoggedIn) {
      const domainParts = window.location.host.split('.');
      const domain = domainParts[domainParts.length - 1];
      const currentLang = this.currentLanguage === 'de' ? '' : `${this.currentLanguage}/`;
      window.open(`https://inyova.${domain}/${currentLang}`, '_blank');
    } else if (this.currentAccount && this.currentAccount.step_status === 5) {
      void this.router.navigateByUrl('/reports/dashboard');
    } else {
      void this.router.navigateByUrl('/');
    }
  }

  logout(): void {
    if (this.activeHamburger) {
      document.body.classList.remove('no-scroll');
    }
    this.authService.logout(false, true);
  }

  toggleMenu(): void {
    // Toggle view
    this.activeHamburger = !this.activeHamburger;
    // Set scroll
    if (this.activeHamburger) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }

  hasAccount(type: string): boolean {
    if (this.customer) {
      return this.customer.temporary_accounts.filter((account) => account.kind === type).length > 0;
    }
    return false;
  }

  findCustomerAccount(kind: '3a' | '3b' | 'grow'): CustomerAccountItem {
    return this.customer.temporary_accounts.find((item) => kind === item.kind);
  }

  createKidsAccount(): void {
    if (!this.customer?.accounts) {
      return;
    }
    const investmentAccount = this.customer.temporary_accounts.filter((item) => item.kind === '3b');
    void this.router.navigateByUrl(
      investmentAccount[0] && isMinAlmostCustomer(investmentAccount[0].step_status) ? `/kids/register` : '/kids/accounts'
    );
    if (this.mobileView) {
      this.toggleMenu();
    }
  }

  checkWasChanged(path = '') {
    if (this.currentAccount?.enabled_features.includes(FeatureFlags.EVE2)) {
      return false;
    }
    if (this.strategyWasChanged?.strategyWasChanged) {
      this.store.dispatch(StrategyActions.setStrategyWasChanged({ modalOpened: true, redirectUrl: path, strategyWasChanged: true }));
      return true;
    }

    return false;
  }

  switchAccount(account: CustomerAccountItem): void {
    if (this.currentAccount?.id !== account.id) {
      this.store.dispatch(
        AppRootActions.setSwitchAccountLoading({
          show: true,
          message: this.translateService.instant(`fullLoader.switchingAccountType.${account.kind === 'kid' ? 'kidsAccount' : account.kind}`, {
            kidsName: account.owner_name
          })
        })
      );
      this.store.dispatch(AppRootActions.switchAccount({ id: account.id, redirectUrl: null }));
    } else {
      localStorage.removeItem('CIAccount');
      this.store.dispatch(AppRootActions.toggleCIAccountActive({ show: false }));
      void this.router.navigateByUrl('/');
    }

    if (this.mobileView) {
      this.toggleMenu();
    }
  }

  switchCIAccount() {
    if (this.mobileView) {
      this.toggleMenu();
    }

    if (this.isCIActive) return;

    this.store.dispatch(AppRootActions.toggleCIAccountActive({ show: true }));
    this.store.dispatch(
      AppRootActions.setSwitchAccountLoading({
        show: true,
        message: this.translateService.instant('fullLoader.switchingAccountType.CI')
      })
    );
    localStorage.setItem('CIAccount', 'active');
    void this.router.navigate(['ci-overview']);
  }

  changeCountry(country: { flag: string; key: string }): void {
    this.selectedCountry = country;
    this.isCountryListOpen = !this.isCountryListOpen;
    this.languages = this.languageService.getListOfLanguages(country.flag as 'ch' | 'de') as [LanguageListItem];

    // update selected lang with the same selected before with diff country code
    const defaultDE = { code: Languages.DE_CH, fullName: 'Deutsch', shortName: 'DE' };
    this.selectedLanguage = this.languages.find((item: LanguageListItem) => item.shortName === this.selectedLanguage.shortName) || defaultDE;
  }

  changeLanguage(language: { code: Languages; fullName: string; shortName: string }): void {
    this.selectedLanguage = language;
    this.isLanguageListOpen = !this.isLanguageListOpen;
  }

  saveCountrySwitch(): void {
    this.isCountrySwitchOpen = !this.isCountrySwitchOpen;

    const host = window.location.hostname;
    const path = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const registerPages = { en: 'create-profile', de: 'profil-erstellen', fr: 'creer-profil' };
    const lang = this.selectedLanguage?.shortName.toLowerCase();
    const finalDomain = host.replace(this.domain, this.selectedCountry?.flag);

    if (this.domain !== this.selectedCountry.flag) {
      let url = '';

      urlParams.delete('lang');
      urlParams.append('lang', this.selectedLanguage.code);

      if (path.includes('prof')) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        url = `//${finalDomain}/${registerPages[lang]}`;
      } else {
        url = `//${finalDomain}${path}?${urlParams.toString()}`;
      }

      window.location.href = url;
    } else {
      this.languageService.setLanguage(this.selectedLanguage.code);
      if (path.includes('prof')) {
        // if it's register redirect correct url
        void this.router.navigate([registerPages[lang]]);
      } else {
        // if there is lang param remove it
        void this.router.navigate([], {
          queryParams: {
            lang: null
          },
          queryParamsHandling: 'merge'
        });
      }
    }
  }

  close(): void {
    this.isCountrySwitchOpen = !this.isCountrySwitchOpen;
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const targetElement = event.target as HTMLElement;
    const dropdownTrigger = document.getElementById('dropdown-trigger');
    if (targetElement && dropdownTrigger && !dropdownTrigger.contains(targetElement)) {
      this.openDropdown = false;
    }

    // lang dropdowns
    const targetClass = (event.target as HTMLElement).className;
    if (targetClass !== 'dropdown-item with-arrow' && targetClass !== 'dropdown-list-item') {
      this.isCountryListOpen = false;
      this.isLanguageListOpen = false;
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: { target: Window }): void {
    if (event.target.innerWidth > (this.currentAccount.step_status === 5 ? MOBILE_NAVIGATION : MOBILE_VIEW_TRESHOLD)) {
      this.mobileView = false;
    } else {
      this.mobileView = true;
    }
  }

  trackActivity(activity: string): void {
    this.trackingService.trackActivity(activity);
  }

  createNewAccount(type: string) {
    if (this.isLoading) {
      return;
    }

    const payload = {
      kind: type,
      appLocation: this.customer.app_location
    };
    localStorage.removeItem('CIAccount');
    this.store.dispatch(AppRootActions.toggleCIAccountActive({ show: false }));
    this.store.dispatch(AppRootActions.toggleNewAccountIsLoading({ isLoading: true }));
    this.store.dispatch(AppRootActions.createNewAccount(payload));
  }

  launchZendesk() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.zendeskService.zE.activate();
  }

  getID() {
    if (!this.isLoggedIn) {
      return 'sl-login';
    }

    if (this.isCIActive) {
      return 'sl-ci';
    }

    if (this.currentAccount.kind) {
      return `sl-${this.currentAccount.kind}`;
    }

    return ``;
  }
}
